var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { cloneSTOrderDetailsReportsPayload, hasInMemorySTOrderDetailsSliceForView, isSTOrderDetailsSliceCachedForPage, mergeStReportsOrdersApiPayload, prepareSTOrderDetailsReportsPayload, ST_ORDER_DETAILS_UI_TAB_STATUSES, } from "../component/model/STOrderDetailsModel";
export function buildSTReportsOrdersCacheRangeKey(dateFrom, dateTo, pageSize, searchQuery) {
    var q = (searchQuery !== null && searchQuery !== void 0 ? searchQuery : "").trim();
    return q ? "".concat(dateFrom, "|").concat(dateTo, "|ps:").concat(pageSize, "|q:").concat(q) : "".concat(dateFrom, "|").concat(dateTo, "|ps:").concat(pageSize);
}
export function getSTReportsCachedPageEntry(cache, rangeKey, tab, pageNumber, pageSize) {
    var _a;
    var bucket = cache === null || cache === void 0 ? void 0 : cache[rangeKey];
    if (!bucket || bucket.pageSize !== pageSize) {
        return null;
    }
    var tabKey = tab;
    var entry = (_a = bucket.pagesByTab[tabKey]) === null || _a === void 0 ? void 0 : _a[pageNumber];
    if (!entry || entry.pageSize !== pageSize || entry.pageNumber !== pageNumber) {
        return null;
    }
    return entry;
}
export function upsertSTReportsOrdersCache(cache, rangeKey, tab, pageNumber, pageSize, searchQuery, apiPayload) {
    var _a, _b, _c, _d;
    var _e, _f, _g, _h;
    var payload = (_e = prepareSTOrderDetailsReportsPayload(apiPayload)) !== null && _e !== void 0 ? _e : apiPayload;
    var existing = cache[rangeKey];
    var bucket = existing !== null && existing !== void 0 ? existing : {
        searchQuery: searchQuery.trim(),
        pageSize: pageSize,
        pagesByTab: {},
        mergedPayload: null,
    };
    var isSearchMode = searchQuery.trim().length > 0;
    var tabKey = tab;
    var tabPages = __assign({}, ((_f = bucket.pagesByTab[tabKey]) !== null && _f !== void 0 ? _f : {}));
    /** Before overwriting merged with page 2+, snapshot page 1 for this tab so "previous" uses cache. */
    if (!isSearchMode && pageNumber > 1 && !tabPages[1]) {
        var pageOneDisplay = getSTReportsDisplayPayloadFromCache(__assign(__assign({}, cache), (_a = {}, _a[rangeKey] = bucket, _a)), rangeKey, tab, 1, pageSize);
        if (pageOneDisplay) {
            tabPages[1] = {
                pageNumber: 1,
                pageSize: pageSize,
                payload: cloneSTOrderDetailsReportsPayload(pageOneDisplay),
            };
        }
    }
    tabPages[pageNumber] = {
        pageNumber: pageNumber,
        pageSize: pageSize,
        payload: cloneSTOrderDetailsReportsPayload(payload),
    };
    var pagesByTab = __assign(__assign({}, bucket.pagesByTab), (_b = {}, _b[tabKey] = tabPages, _b));
    /** Initial `All` page 1 fills every tab — store a page-1 entry per tab for later prev/next navigation. */
    if (!isSearchMode && tab === "All" && pageNumber === 1) {
        var pageOnePayload = cloneSTOrderDetailsReportsPayload(payload);
        for (var _i = 0, ST_ORDER_DETAILS_UI_TAB_STATUSES_1 = ST_ORDER_DETAILS_UI_TAB_STATUSES; _i < ST_ORDER_DETAILS_UI_TAB_STATUSES_1.length; _i++) {
            var uiTab = ST_ORDER_DETAILS_UI_TAB_STATUSES_1[_i];
            if (!isSTOrderDetailsSliceCachedForPage(pageOnePayload, uiTab, 0, pageSize)) {
                continue;
            }
            var uiTabKey = uiTab;
            var existingTabPages = __assign({}, ((_g = pagesByTab[uiTabKey]) !== null && _g !== void 0 ? _g : {}));
            if (!existingTabPages[1]) {
                existingTabPages[1] = {
                    pageNumber: 1,
                    pageSize: pageSize,
                    payload: cloneSTOrderDetailsReportsPayload(pageOnePayload),
                };
                pagesByTab = __assign(__assign({}, pagesByTab), (_c = {}, _c[uiTabKey] = existingTabPages, _c));
            }
        }
    }
    var mergedRaw = isSearchMode
        ? payload
        : mergeStReportsOrdersApiPayload(bucket.mergedPayload, payload, tab, { isSearchMode: false });
    var mergedPayload = (_h = prepareSTOrderDetailsReportsPayload(mergedRaw)) !== null && _h !== void 0 ? _h : mergedRaw;
    return __assign(__assign({}, cache), (_d = {}, _d[rangeKey] = {
        searchQuery: searchQuery.trim(),
        pageSize: pageSize,
        pagesByTab: pagesByTab,
        mergedPayload: cloneSTOrderDetailsReportsPayload(mergedPayload),
    }, _d));
}
/**
 * Builds grid payload for `stReportsData` from Redux cache (direct tab/page entry or merged bootstrap slice).
 */
export function getSTReportsDisplayPayloadFromCache(cache, rangeKey, tab, pageNumber, pageSize) {
    var _a, _b;
    var bucket = cache === null || cache === void 0 ? void 0 : cache[rangeKey];
    if (!bucket || bucket.pageSize !== pageSize) {
        return null;
    }
    var direct = getSTReportsCachedPageEntry(cache, rangeKey, tab, pageNumber, pageSize);
    if (direct) {
        var merged_1 = mergeStReportsOrdersApiPayload(bucket.mergedPayload, direct.payload, tab, {
            isSearchMode: false,
        });
        var display = (_a = prepareSTOrderDetailsReportsPayload(merged_1)) !== null && _a !== void 0 ? _a : merged_1;
        return cloneSTOrderDetailsReportsPayload(display);
    }
    var merged = bucket.mergedPayload;
    if (merged && hasInMemorySTOrderDetailsSliceForView(merged, tab, pageNumber - 1, pageSize)) {
        var display = (_b = prepareSTOrderDetailsReportsPayload(merged)) !== null && _b !== void 0 ? _b : merged;
        return cloneSTOrderDetailsReportsPayload(display);
    }
    return null;
}
export function hasSTReportsDisplayPayloadInCache(cache, rangeKey, tab, pageNumber, pageSize) {
    return getSTReportsDisplayPayloadFromCache(cache, rangeKey, tab, pageNumber, pageSize) != null;
}
/** True when this exact `dateFrom|dateTo` bucket was populated by a prior API response (including zero rows). */
export function hasSTReportsExactRangeCacheAuthority(cache, rangeKey, pageSize) {
    var _a;
    var bucket = cache === null || cache === void 0 ? void 0 : cache[rangeKey];
    if (!bucket || bucket.pageSize !== pageSize) {
        return false;
    }
    if (bucket.mergedPayload != null) {
        return true;
    }
    var pagesByTab = (_a = bucket.pagesByTab) !== null && _a !== void 0 ? _a : {};
    return Object.values(pagesByTab).some(function (tabPages) { return tabPages != null && Object.keys(tabPages).length > 0; });
}
